/**
 * UtilityIntelLayer — Iter 37 Utility Intelligence V1.
 *
 * Restrained SVG overlay rendering:
 *   - Hotspot rings   (soft amber tint circle, behind everything)
 *   - Crossing connectors (thin dashed line between paired structures)
 *   - Crossing diamonds   (small ◆ glyph at the midpoint, tap target)
 *
 * Drawing-First: nothing here ever dominates. Diamonds are 1.4 svg units
 * (~1.4% of sheet width) — visible but never loud. Tap → opens the
 * ConflictInspector via the onPick callback. Pointer events are surgical
 * — only the diamond consumes taps, the rest of the SVG is transparent.
 *
 * Renders inside SheetCanvas's transformed wrapper (viewBox 0..100,
 * preserveAspectRatio="none") so the markers track pan/zoom with the
 * structures + measurements + revision clouds.
 */
import React, { useEffect, useState } from "react";
import { fetchUtilityIntel, SEVERITY_TONE } from "@/lib/utilityIntel";


export default function UtilityIntelLayer({ sheetId, visible, onPick, focusedCrossingId }) {
  const [data, setData] = useState({ crossings: [], hotspots: [], pipe_runs: [], line_crossings: [] });

  useEffect(() => {
    if (!sheetId) return;
    const ac = new AbortController();
    fetchUtilityIntel(sheetId, { signal: ac.signal }).then((d) => {
      if (d) setData(d);
    });
    return () => ac.abort();
  }, [sheetId]);

  if (!visible || !sheetId) return null;
  const { crossings = [], hotspots = [], pipe_runs = [], line_crossings = [] } = data;
  if (!crossings.length && !hotspots.length && !pipe_runs.length && !line_crossings.length) return null;

  // Iter 45 — line geometry tones per layer. Soft, restrained, runs are
  // 0.18 stroke width so they sit BENEATH structures and crossings.
  const RUN_TONE = {
    storm:       "rgba(96,165,250,0.55)",   // soft blue
    sanitary:    "rgba(168,127,80,0.55)",   // soft brown
    water:       "rgba(56,189,248,0.55)",   // cyan
    force_main:  "rgba(216,160,40,0.55)",   // ochre
    electrical:  "rgba(232, 93, 31,0.55)",  // operational orange
  };

  return (
    <svg
      className="absolute inset-0"
      style={{ pointerEvents: "none" }}
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      width="100%"
      height="100%"
      data-testid="utility-intel-layer"
      data-crossing-count={crossings.length}
      data-hotspot-count={hotspots.length}
      data-pipe-run-count={pipe_runs.length}
      data-line-crossing-count={line_crossings.length}
    >
      {/* Iter 45 — inferred pipe runs. Thin polylines under everything else. */}
      {pipe_runs.map((run) => {
        const tone = RUN_TONE[run.layer_type] || "rgba(148,163,184,0.55)";
        const d = run.points.map((p, i) =>
          `${i === 0 ? "M" : "L"} ${(p.nx * 100).toFixed(3)} ${(p.ny * 100).toFixed(3)}`
        ).join(" ");
        return (
          <path
            key={run.id}
            d={d}
            fill="none"
            stroke={tone}
            strokeWidth={0.22}
            strokeLinecap="round"
            strokeLinejoin="round"
            opacity={0.7}
            data-testid={`pipe-run-${run.id}`}
            data-layer={run.layer_type}
            data-segment-count={run.segment_count}
          />
        );
      })}

      {/* Hotspot rings — behind everything else. Soft, restrained. */}
      {hotspots.map((h) => (
        <g key={h.id} data-testid={`hotspot-${h.id}`} data-count={h.crossing_count}>
          <circle
            cx={h.nx * 100}
            cy={h.ny * 100}
            r={h.radius_norm * 100}
            fill="rgba(245,158,11,0.07)"
            stroke="rgba(245,158,11,0.45)"
            strokeWidth={0.18}
            strokeDasharray="0.6 0.6"
          />
        </g>
      ))}

      {/* Iter 45 — line × line crossings. Small × marker at intersection. */}
      {line_crossings.map((cx) => {
        const tone = SEVERITY_TONE[cx.severity] || SEVERITY_TONE.watch;
        const mx = cx.intersection.nx * 100;
        const my = cx.intersection.ny * 100;
        const r = 1.0;
        return (
          <g key={cx.id} data-testid={`line-crossing-${cx.id}`}
             data-severity={cx.severity}
             data-layers={`${cx.layer_a}-x-${cx.layer_b}`}>
            {/* × marker, two diagonal lines */}
            <line
              x1={mx - r} y1={my - r} x2={mx + r} y2={my + r}
              stroke={tone.color} strokeWidth={0.30} opacity={0.85}
            />
            <line
              x1={mx - r} y1={my + r} x2={mx + r} y2={my - r}
              stroke={tone.color} strokeWidth={0.30} opacity={0.85}
            />
            {/* Tap target — invisible circle slightly larger than the ×. */}
            <circle
              cx={mx} cy={my} r={1.4}
              fill={tone.soft}
              stroke="none"
              style={{ pointerEvents: "auto", cursor: "pointer" }}
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => { e.stopPropagation(); onPick && onPick(cx); }}
            />
          </g>
        );
      })}

      {/* Crossings — connector + diamond. */}
      {crossings.map((c) => {
        const tone = SEVERITY_TONE[c.severity] || SEVERITY_TONE.watch;
        const ax = (c.a.normalized_x || 0) * 100;
        const ay = (c.a.normalized_y || 0) * 100;
        const bx = (c.b.normalized_x || 0) * 100;
        const by = (c.b.normalized_y || 0) * 100;
        const mx = c.midpoint.nx * 100;
        const my = c.midpoint.ny * 100;
        const focused = focusedCrossingId === c.id;
        const r = focused ? 1.6 : 1.2; // diamond half-extent in svg units
        return (
          <g key={c.id} data-testid={`utility-crossing-${c.id}`} data-severity={c.severity} data-focused={focused ? "1" : undefined}>
            {/* Connector. Thin dashed line so it never overpowers the drawing. */}
            <line
              x1={ax} y1={ay} x2={bx} y2={by}
              stroke={tone.color}
              strokeWidth={focused ? 0.22 : 0.14}
              strokeDasharray="0.5 0.45"
              opacity={focused ? 0.85 : 0.55}
            />
            {/* Diamond — tap target. Pointer events ONLY on this. */}
            <g
              transform={`translate(${mx} ${my})`}
              style={{ pointerEvents: "auto", cursor: "pointer" }}
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => { e.stopPropagation(); onPick && onPick(c); }}
            >
              <polygon
                points={`0,${-r} ${r},0 0,${r} ${-r},0`}
                fill={tone.soft}
                stroke={tone.color}
                strokeWidth={focused ? 0.32 : 0.22}
              />
              {focused && (
                <polygon
                  points={`0,${-(r + 0.8)} ${r + 0.8},0 0,${r + 0.8} ${-(r + 0.8)},0`}
                  fill="none"
                  stroke={tone.color}
                  strokeWidth={0.12}
                  opacity={0.55}
                />
              )}
            </g>
          </g>
        );
      })}
    </svg>
  );
}


// Reload hook — exposes a refetch() the parent can call after a structure
// edit. V1 reloads on sheet change only; future iterations can wire mutation
// callbacks to refresh just the intel layer without a full structures GET.
export function useUtilityIntel(sheetId) {
  const [data, setData] = useState({ crossings: [], hotspots: [], pipe_runs: [], line_crossings: [] });
  useEffect(() => {
    if (!sheetId) return;
    const ac = new AbortController();
    fetchUtilityIntel(sheetId, { signal: ac.signal }).then((d) => {
      if (d) setData(d);
    });
    return () => ac.abort();
  }, [sheetId]);
  return data;
}
